import { selectOrdinal, t } from "@lingui/core/macro";
import { orderedCategories } from "@medoma/models/activities";
import {
  orderedCompetences,
  type IMedicalCompetence,
} from "@medoma/models/shifts";
import { isActivityTemplate, isBatchTemplate } from "@medoma/models/templates";
import {
  type IActivityTemplate,
  type IBatchTemplate,
  type ITemplate,
} from "@/api/Templates";
import {
  recurringDictionary,
  recurringOptionSchema,
  weekdayDictionary,
} from "./Scheduling/schedulingModels";
import type { IBatchTemplateActivity } from "./batchTemplateFormSchema";

const getTemplateCompetencePriority = (
  competences: IMedicalCompetence[] | undefined | null,
): number => {
  if (!competences || competences.length === 0) {
    return 999;
  }
  const priorities = competences
    .map((competence) => orderedCompetences.indexOf(competence))
    .filter((index) => index !== -1);
  return priorities.length === 0 ? 999 : Math.min(...priorities);
};

export const sortActivityTemplates = (
  templates: IActivityTemplate[],
): IActivityTemplate[] =>
  [...templates].sort((a, b) => {
    const aCategoryIndex = orderedCategories.indexOf(a.template.category);
    const bCategoryIndex = orderedCategories.indexOf(b.template.category);
    if (aCategoryIndex !== bCategoryIndex) {
      return aCategoryIndex - bCategoryIndex;
    }
    const aPriority = getTemplateCompetencePriority(
      a.template.requiredCompetences,
    );
    const bPriority = getTemplateCompetencePriority(
      b.template.requiredCompetences,
    );
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }
    return a.template.title.localeCompare(b.template.title);
  });

export const sortBatchTemplates = (
  templates: IBatchTemplate[],
): IBatchTemplate[] =>
  [...templates].sort((a, b) =>
    a.template.title.localeCompare(b.template.title),
  );

export const partitionTemplates = (
  templates: ITemplate[],
): { batches: IBatchTemplate[]; activities: IActivityTemplate[] } => ({
  batches: templates.filter(isBatchTemplate),
  activities: templates.filter(isActivityTemplate),
});

export const getRecurrenceLabel = ({
  recurrence,
  customRecurrenceInterval,
  customRecurrenceIntervalUnit,
  weekdays,
}: Pick<
  IBatchTemplateActivity,
  | "recurrence"
  | "customRecurrenceInterval"
  | "customRecurrenceIntervalUnit"
  | "weekdays"
>) => {
  if (!recurrence) {
    return undefined;
  }

  if (recurrence === recurringOptionSchema.enum.custom) {
    if (!customRecurrenceInterval || !customRecurrenceIntervalUnit) {
      return undefined;
    }

    const intervalAsNumber = Number(customRecurrenceInterval);
    if (isNaN(intervalAsNumber) || intervalAsNumber < 1) {
      return undefined;
    }

    if (customRecurrenceIntervalUnit === "weeks") {
      return selectOrdinal(customRecurrenceInterval, {
        1: "Varje vecka",
        2: "Varannan vecka",
        one: "Var #:a vecka",
        two: "Var #:a vecka",
        few: "Var #:e vecka",
        other: "Var #:e vecka",
      });
    }
    if (customRecurrenceIntervalUnit === "days") {
      return selectOrdinal(customRecurrenceInterval, {
        1: "Varje dag",
        2: "Varannan dag",
        one: "Var #:a dag",
        two: "Var #:a dag",
        few: "Var #:e dag",
        other: "Var #:e dag",
      });
    }
  }

  if (
    recurrence === recurringOptionSchema.enum.onSelectedWeekdays &&
    weekdays
  ) {
    if (weekdays?.length === 0) {
      return t`Välj dagar`;
    }
    return weekdays
      .map((day) => t(weekdayDictionary[day].regular).toLocaleLowerCase())
      .join(", ");
  }

  return t(recurringDictionary[recurrence]);
};
